import React from 'react';
import { MainContextProvider } from 'context';
import { createRoot } from 'react-dom';
import './index.scss';
import 'styles/globals.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import client from 'utils/api-client';
import { SignIn } from 'views/SignInPage/SignInPage';
import { HelmetProvider } from 'react-helmet-async';
import {
  Home,
  Join,
  Role,
  SignUp,
  Menu,
  SDG,
  Goal,
  Term,
  Reset,
  Confirm,
  VerifyEmail,
  FourOhFour,
  Presentations,
  Tutorial,
  Tutorials,
} from 'views';
import AdminRoutes from 'views/admin';
import DigitalSignUp from 'views/DigitalSignUp';
import ScrollToTop from 'hoc/ScrollToTop';
import reportWebVitals from './reportWebVitals';
import { AboutPage } from 'views/Pages/aboutPage/AboutPage';
import { WorkPage } from 'views/Pages/workPage/WorkPage';
import { DashboardRoutes } from './views/DashboardRoutes';
import DisconnectHandlerWrapper from './utils/disconnectHandler';
import GoogleMetrics from './utils/googleMetrics';
import RootLoaders from './utils/rootLoaders';
import { MetaMaskProvider } from 'metamask-react';
import PdfViewPage from './views/PDFs/PdfViewPage';
import { ConfigProvider } from 'tt-ui-lib/core';
import 'tt-ui-lib/core/style.css';

import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const endpointDA = process.env.REACT_APP_DIGITAL_ASSETS;

const ROUTES_INFO = [
  {
    path: '/',
    element: <Home />,
  },
  {
    path: 'join',
    element: <Join />,
  },
  {
    path: 'join/:role',
    element: <Role />,
  },
  {
    path: 'join/:role/sign_up',
    element: <SignUp />,
  },
  {
    path: 'join/digital/sign_up',
    element: <DigitalSignUp />,
  },
  {
    path: 'sign_in',
    element: <SignIn />,
  },
  {
    path: 'reset',
    element: <Reset />,
  },
  {
    path: 'menu',
    element: <Menu />,
  },
  {
    path: 'confirm',
    element: <Confirm />,
  },
  {
    path: 'verify-email',
    element: <VerifyEmail />,
  },
  {
    path: 'sdgs',
    element: <SDG />,
  },
  {
    path: 'sdgs/:goal',
    element: <Goal />,
  },
  {
    path: '/:term',
    element: <Term />,
  },
  {
    path: '*',
    element: <FourOhFour />,
  },
  {
    path: 'about',
    element: <AboutPage />,
  },
  {
    path: 'work',
    element: <WorkPage />,
  },
  {
    path: '/presentations',
    element: <Presentations />,
  },
  {
    path: '/tutorials',
    element: <Tutorials />,
  },
  {
    path: '/tutorial/:id',
    element: <Tutorial />,
  },

  {
    path: '/dashboard/*',
    element: <DashboardRoutes />,
  },
  {
    path: '/certificate/:uid',
    element: <Navigate to={`${endpointDA}/certificate`} />,
  },

  {
    path: '/FCEchain',
    element: (
      <PdfViewPage fileUrl="https://prod-sso-public-tt.s3.eu-central-1.amazonaws.com/doc/FCEchain.pdf" />
    ),
  },
  {
    path: '/FCE2022',
    element: (
      <PdfViewPage fileUrl="https://prod-sso-public-tt.s3.eu-central-1.amazonaws.com/doc/FCE2022.pdf" />
    ),
  },
  // {
  //   path: '/draft/:id',
  //   element: <DraftPage />,
  // },

  // Admin
  {
    path: '/admin/*',
    element: <AdminRoutes />,
  },
];

const helmetContext = {};
const rootElement = document.getElementById('root');

createRoot(rootElement).render(
  // <React.StrictMode>
  <Elements stripe={stripePromise}>
    <BrowserRouter>
      <HelmetProvider context={helmetContext}>
        <ApolloProvider client={client}>
          <MetaMaskProvider>
            <MainContextProvider>
              <ConfigProvider>
                <GoogleMetrics />
                <DisconnectHandlerWrapper />
                <RootLoaders />
                <ScrollToTop />
                <Routes>
                  {ROUTES_INFO.map((routeInfo) => (
                    <Route path={routeInfo.path} element={routeInfo.element} key={routeInfo.path} />
                  ))}
                </Routes>
              </ConfigProvider>
            </MainContextProvider>
          </MetaMaskProvider>
        </ApolloProvider>
      </HelmetProvider>
    </BrowserRouter>
  </Elements>,

  // </React.StrictMode>,
  rootElement
);

reportWebVitals();
